import { Building2, Mail, Phone, User, MessageCircle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, StatusBadge } from "@/components/common";
import { AccountsTab, AuditTab, CustomersTab, DevicesTab, FleetAdminTab, UsageTab } from "@/components/admin/AdminTabs";
import { useAuth } from "@/context/AuthContext";
import { useFleet } from "@/context/FleetContext";
import { vehicleStatus } from "@/lib/fleet";

function CustomerAccount() {
  const { user } = useAuth();
  const { vehicles } = useFleet();
  const c = user.customer || {};
  const renewal = user.renewal_contact || {};
  const subscriptions = user.vehicles_subscription || [];
  const whatsapp = (renewal.whatsapp || "").replace(/\\D/g, "");
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[380px_1fr]">
      <div className="gt-card space-y-4 p-5" data-testid="customer-account-card">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-lg font-extrabold text-primary-foreground">{user.name[0]}</span>
          <div><div className="font-bold">{user.name}</div><div className="text-xs text-muted-foreground">Customer · {c.name}</div></div>
        </div>
        {[[Mail, user.email], [Building2, c.company || c.name], [Phone, c.phone || "-"], [User, c.contact || "-"]].map(([I, t], i) => (
          <div key={i} className="flex items-center gap-3 text-sm"><I className="h-4 w-4 text-muted-foreground" /> {t}</div>
        ))}
      </div>
      <div className="gt-card space-y-3 p-4" data-testid="customer-renewal-card">
        <div className="font-bold">Status masa aktif dan perpanjangan</div>
        {subscriptions.length ? subscriptions.map((v) => (
          <div key={v.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-2 text-sm">
            <span>{v.name} · {v.plate}</span>
            <span className="text-muted-foreground">
              {v.subscription_status === "active" ? `Aktif s/d ${v.expires_at}` :
               v.subscription_status === "expired" ? `Kedaluwarsa (${v.expires_at})` : "Belum diaktifkan"}
            </span>
          </div>
        )) : <div className="text-sm text-muted-foreground">Belum ada data masa aktif kendaraan.</div>}
        <div className="text-sm text-muted-foreground">
          {renewal.name ? `Admin penanggung jawab: ${renewal.name}` : "Admin penanggung jawab belum ditentukan."}
        </div>
        {whatsapp && (
          <a
            href={`https://wa.me/${whatsapp}?text=${encodeURIComponent("Halo, saya ingin menanyakan perpanjangan masa aktif GPS tracker.")}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700"
            data-testid="renewal-whatsapp-btn"
          >
            <MessageCircle className="h-4 w-4" /> Hubungi Admin via WhatsApp
          </a>
        )}
      </div>
      <div className="gt-card p-2" data-testid="customer-vehicle-tree">
        <div className="px-3 py-2 text-sm font-bold">{c.name} <span className="font-mono text-xs text-muted-foreground">· {vehicles.length} kendaraan</span></div>
        {vehicles.map((v, i) => (
          <div key={v.id} className="flex items-center justify-between px-3 py-2 text-sm">
            <span className="font-mono text-muted-foreground">{i === vehicles.length - 1 ? "└──" : "├──"} <span className="font-sans font-semibold text-foreground">{v.name}</span> <span className="text-xs">{v.plate} · IMEI {v.imei}</span></span>
            <StatusBadge status={vehicleStatus(v)} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function UsersPage() {
  const { isAdmin, user } = useAuth();
  if (user?.role === "seller")
    return (
      <div>
        <PageHeader eyebrow="Customer" title="Customer" subtitle="Kelola customer yang berada di bawah tanggung jawab Anda." />
        <div className="mt-5">
          <CustomersTab />
        </div>
      </div>
    );
  if (user?.role === "customer")
    return (
      <div>
        <PageHeader eyebrow="Pengguna" title="Akun Saya" subtitle="Informasi akun dan kendaraan yang terhubung ke akun Anda." />
        <CustomerAccount />
      </div>
    );
  const tabs = [["customers", "Customer", CustomersTab], ["fleet", "Kendaraan & Masa Aktif", FleetAdminTab], ["devices", "Perangkat GPS", DevicesTab], ["accounts", "Akun", AccountsTab], ["audit", "Audit Log", AuditTab], ["usage", "Penggunaan Sistem", UsageTab]];
  return (
    <div>
      <PageHeader eyebrow="Admin Panel" title="Pengguna & Administrasi" subtitle="Kelola customer, kendaraan, IMEI, masa aktif, akun, audit log dan penggunaan sistem." />
      <Tabs defaultValue="customers">
        <TabsList className="gt-noscroll mb-5 h-auto w-full justify-start overflow-x-auto rounded-2xl border border-border bg-card p-1">
          {tabs.map(([k, l]) => <TabsTrigger key={k} value={k} className="rounded-xl px-4 py-2 text-[13px] font-semibold data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid={`admin-tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
        {tabs.map(([k, , C]) => <TabsContent key={k} value={k}><C /></TabsContent>)}
      </Tabs>
    </div>
  );
}
